import React, { FC } from 'react';
import { useRouter } from 'next/router';
import ApplyModal from '@components/phs/test/modals/apply';
import ApplyConfirmModal from '@components/phs/test/modals/applyConfirm';
import NoMoreChanceModal from '@components/phs/test/modals/noMoreChance';
import TestGuideModal from '@components/phs/test/paccGate/modals/testGuide';
import TutorialGuideModal from '@components/phs/test/paccGate/modals/tutorialGuide';
import TutorialTermsModal from '@components/phs/test/modals/tutorialTerms';
import TestTermsModal from '@components/phs/test/modals/testTerms';
import { useApplyPCPTermsCheckNew, useApplyPPPTermsCheckNew } from '@repository/userTermsAgrees/useUserTermsAgrees';
import VerificationModal from '@components/phs/test/modals/verification';
import { useAchievementLog } from '@repository/match/logging/useLogging';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { PhsModalType } from '@domain/constant/phsModalType';
import useDebounce from '@utils/hooks/useDebounce';
import { V2Route } from '@utils/routeUtil';
import { queryClient } from '@utils/queryClient';
import UrlUtil from '@utils/urlUtil';
import { useToast } from '@components/common/toast';
import { PACCApplyStatusKeys, usePACCApplyStatus } from '@repository/phs/pacc/apply/usePhsPaccApply';
import { usePHSApply, usePHSTutorialApply } from '@repository/phs/apply/usePhsApply';

interface IPHSPACCModalProps {
  modalState: PhsModalType;
  setModalState: (type: PhsModalType) => void;
}

const PHSPACCModal: FC<IPHSPACCModalProps> = ({ modalState, setModalState }) => {
  const Toast = useToast();
  const { query } = useRouter();
  const isVisible = (modalType: PhsModalType) => modalType === modalState;
  const { data: userInfo } = useUserInfo();
  const userName = userInfo?.name ?? '';
  const isLogin = !!userInfo?.id;
  const { data } = usePACCApplyStatus(isLogin);

  const { mutateAsync: saveAchievementLogAsync } = useAchievementLog();
  const { mutateAsync: pSTApplyMutateAsync } = usePHSApply();
  const { mutateAsync: pACCTutorialApplyMutateAsync } = usePHSTutorialApply();
  const { mutateAsync: checkApplyPCPTermsMutateAsync } = useApplyPCPTermsCheckNew();
  const { mutateAsync: checkApplyPPPTermsMutateAsync } = useApplyPPPTermsCheckNew();

  const onClose = () => {
    setModalState(null);
  };

  //개발자 역량검사 이어하기
  const onContinueTest = useDebounce(() => {
    if (!data || !data.lastApplyStatus) return null;
    const { code } = data.lastApplyStatus;

    UrlUtil.openWindowWithPost(V2Route.getPHSApplyApiPage(data.url), {
      examCode: code,
      name: userName,
    });
    query.source_page === 'MATCH' &&
      saveAchievementLogAsync({ achievementType: 'PACC_APPLY', sourcePage: query.source_page });

    onClose();
  }, 1000);

  //응시 하기 버튼 이벤트
  const onApplyTest = useDebounce(async () => {
    const rs = await pSTApplyMutateAsync(
      { paccYn: true, pstYn: false },
      {
        onSuccess: () => {
          query.source_page === 'MATCH' &&
            saveAchievementLogAsync({ achievementType: 'PACC_APPLY', sourcePage: query.source_page });
        },
        onError: () => {
          Toast({
            type: 'error',
            iconType: 'info',
            content: (
              <>
                새로운 문제를 준비하고 있습니다.
                <br />
                조금만 기다려주세요.
              </>
            ),
          });
        },
      },
    );

    UrlUtil.openWindowWithPost(V2Route.getPHSApplyApiPage(rs.url), {
      examCode: rs.code,
      name: userName,
    });

    //최근 응시 상태 최신화
    await queryClient.refetchQueries(PACCApplyStatusKeys, { active: true, exact: true });
    onClose();
  }, 1000);

  //처음 하기 확인 Modal onConfirm 이벤트
  const onConfirmApplyConfirmModal = useDebounce(async () => {
    //약관 동의 여부 체크
    const applyPCPRs = await checkApplyPCPTermsMutateAsync();
    const applyPPPRs = await checkApplyPPPTermsMutateAsync();
    if (applyPCPRs.existsNewTermsAgree || applyPPPRs.existsNewTermsAgree) {
      setModalState('TEST_TERMS_DIRECT');
      return;
    }

    await onApplyTest();
  }, 1000);

  //튜토리얼 응시 하기 버튼 이벤트
  const onApplyTutorial = useDebounce(async () => {
    const rs = await pACCTutorialApplyMutateAsync({ paccYn: true, pstYn: false });

    UrlUtil.openWindowWithPost(V2Route.getPHSApplyApiPage(rs.url), {
      examCode: rs.code,
      name: userName,
    });

    onClose();
  }, 1000);

  return (
    <>
      {/* 본인 인증 모달 */}
      <VerificationModal visible={isVisible('VERIFICATION')} onClose={onClose} />
      {/* 응시 약관 동의 모달 */}
      <TestTermsModal
        visible={isVisible('TEST_TERMS')}
        onConfirm={() => setModalState('TEST_GUIDE')}
        onClose={onClose}
      />
      {/* 응시 약관 동의 모달(바로 응시되도록 함)  */}
      <TestTermsModal visible={isVisible('TEST_TERMS_DIRECT')} onConfirm={onApplyTest} onClose={onClose} />
      {/* 응시하기 모달 (이어하기, 처음부터 하기)*/}
      <ApplyModal
        type={'PACC'}
        visible={isVisible('APPLY')}
        onClose={onClose}
        onFirstApply={() => setModalState('APPLY_CONFIRM')}
        onContinue={onContinueTest}
        isDisabledContinueBtn={!data?.isProgress}
        vo={data}
      />
      {/* 처음부터 하기 확인 모달 */}
      <ApplyConfirmModal
        title="개발자 역량검사 응시하기"
        visible={isVisible('APPLY_CONFIRM')}
        onConfirm={onConfirmApplyConfirmModal}
        onClose={onClose}
      />
      {/* 응시기회 소진 모달 */}
      <NoMoreChanceModal visible={isVisible('NO_MORE_CHANCE')} onClose={onClose} />

      {/*응시 가이드 모달*/}
      <TestGuideModal visible={isVisible('TEST_GUIDE')} onClose={onClose} onApply={onApplyTest} />

      {/*응시 튜토리얼 가이드 모달*/}
      <TutorialGuideModal visible={isVisible('TUTORIAL_GUIDE')} onClose={onClose} onApply={onApplyTutorial} />

      {/*응시 튜토리얼 약관 모달*/}
      <TutorialTermsModal
        visible={isVisible('TUTORIAL_TERMS')}
        onClose={onClose}
        onConfirm={() => setModalState('TUTORIAL_GUIDE')}
      />
    </>
  );
};

export default PHSPACCModal;
